import { useMemo } from 'react';
import Section from '../shared/Section';
import Btn from '../shared/Btn';
import ServiceLineGrid from '../shared/ServiceLineGrid';
import { useFeeSchedule } from '../../../hooks/useFeeSchedule';

/**
 * Wraps ServiceLineGrid with the section header + Re-sync to schedule
 * banner when payer changed and any non-MANUAL line diverges from contract.
 *
 * Always calls onChange(wholeArray) per useClaimDraft's nested-array
 * invariant; never path-mutates a single line.
 *
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 * }} props
 */
export default function LineItemsSection({ claim, setField, editable, errorsByField }) {
  const fee = useFeeSchedule(claim.payer_snapshot?.payer_id);
  const driftCount = useMemo(() => {
    let n = 0;
    for (const l of claim.service_lines || []) {
      if (!l.cpt_hcpcs_code || !l.charge_amount) continue;
      const r = fee.resolveCharge(l.cpt_hcpcs_code);
      // No schedule entry → nothing to diverge from; skip.
      if (r.amount == null) continue;
      if (String(l.charge_amount) !== String(r.amount)) n += 1;
    }
    return n;
  }, [claim.service_lines, fee]);

  const resync = () => {
    const next = (claim.service_lines || []).map((/** @type {any} */ l) => {
      if (!l.cpt_hcpcs_code) return l;
      const r = fee.resolveCharge(l.cpt_hcpcs_code);
      // No schedule entry → leave the line untouched.
      if (r.amount == null) return l;
      return { ...l, charge_amount: r.amount };
    });
    setField('service_lines', next);
  };

  // Claim-level rendering NPI is derived from service_lines for OVERRIDE/MATCH badge logic.
  const populated = (claim.service_lines || []).map((/** @type {any} */ l) => l.rendering_provider_npi).filter(Boolean);
  const claimLevelNpi = populated.length === 0 ? '' :
    (populated.every((/** @type {string} */ n) => n === populated[0]) ? populated[0] : '');

  return (
    <Section icon="list-checks" title="Service Lines" subtitle="Box 24 — line-level CPT, charges, NPI">
      {editable && driftCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: 10, marginBottom: 12,
          background: 'var(--ab-pending-soft)',
          borderLeft: '3px solid var(--ab-pending)',
          borderRadius: 'var(--ab-radius-sm)',
          fontSize: 13,
        }}>
          <div style={{ flex: 1 }}>
            Payer changed — {driftCount} line{driftCount > 1 ? 's' : ''} diverge from contract.
          </div>
          <Btn kind="secondary" size="sm" onClick={resync}>Re-sync to schedule</Btn>
        </div>
      )}
      <ServiceLineGrid
        lines={claim.service_lines || []}
        payerId={claim.payer_snapshot?.payer_id}
        claimRenderingNpi={claimLevelNpi}
        claimDateOfService={claim.service_lines?.[0]?.service_date_from || claim.service_date_from || ''}
        onChange={(/** @type {any} */ next) => setField('service_lines', next)}
        mode="guided"
        disabled={!editable}
      />
    </Section>
  );
}
